import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    IconButton,
    Popover,
    type SxProps,
    TextField,
    type Theme,
    Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import RequiredAsterisk from '@/components/RequiredAsterisk';
import { errorSnackbar } from '@/utils/snackbar';

type EditableFullnameProps = {
    id: string | undefined;
    open: boolean;
    anchorEl: any;
    firstName: string;
    middleName: string;
    lastName: string;
    onClose: () => void;
    onChange: (name: string, val: string) => void;
    handleValueChange: (name: string, val: string) => void;
};

export default function EditableFullname({
    id,
    open,
    anchorEl,
    onClose,
    firstName,
    middleName,
    lastName,
    onChange,
    handleValueChange,
}: EditableFullnameProps) {
    return (
        <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
        >
            <Typography sx={{ p: 2, minWidth: '300px' }}>
                <Typography sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Typography>
                <FullnameTextbox
                    label="First name"
                    name="firstName"
                    value={firstName}
                    onChange={onChange}
                    handleValueChange={handleValueChange}
                    cantBeEmpty={true}
                />
                <FullnameTextbox
                    label="Middle name"
                    name="middleName"
                    value={middleName}
                    handleValueChange={handleValueChange}
                    onChange={onChange}
                />
                <FullnameTextbox
                    label="Last name"
                    name="lastName"
                    value={lastName}
                    handleValueChange={handleValueChange}
                    onChange={onChange}
                    cantBeEmpty={true}
                />
            </Typography>
        </Popover>
    );
}

type FullnameTextboxProps = {
    label: string;
    value: string;
    name: string;
    cantBeEmpty?: boolean;
    onChange: (name: string, val: string) => void;
    handleValueChange: (name: string, val: string) => void;
};

function FullnameTextbox({
    value,
    name,
    label,
    onChange,
    cantBeEmpty = false,
    handleValueChange,
}: FullnameTextboxProps) {
    const [inputedVal, setInputVal] = useState(value);
    const updateRef = useRef<number | undefined>(undefined);
    const containerStyle: SxProps<Theme> = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingBottom: '10px',
        width: '100%',
        gap: '20px',
    };

    const fieldStyle: SxProps<Theme> = {
        marginTop: '10px',
        width: '100%',
        fontSize: '14px',
        '& .MuiOutlinedInput-input': {
            padding: '8px 12px',
        },
    };
    const labelContent = (
        <>
            {cantBeEmpty ? <RequiredAsterisk /> : ''}
            {label}
        </>
    );
    useEffect(() => {
        window.clearTimeout(updateRef.current);
        if (cantBeEmpty && !inputedVal) {
            errorSnackbar(`${label} cannot be empty!`);
            return;
        }

        updateRef.current = window.setTimeout(() => {
            onChange(name, inputedVal);
            handleValueChange(name, inputedVal);
        }, 1000);
    }, [inputedVal, cantBeEmpty, name, label]);

    return (
        <Box sx={containerStyle}>
            <TextField
                label={labelContent}
                sx={fieldStyle}
                value={inputedVal || ''}
                onChange={(e) => setInputVal(e.target.value)}
            />
        </Box>
    );
}
